import { useState, useEffect } from "react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import PasswordProtection from "@/components/PasswordProtection";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileText, AlertTriangle, Download } from "lucide-react";
import { toast } from "sonner";


export default function Relatorios({ onLogout }) {
  const [turmas, setTurmas] = useState([]);
  const [selectedTurma, setSelectedTurma] = useState("");
  const [relatorio, setRelatorio] = useState(null);
  const [alunosFaltosos, setAlunosFaltosos] = useState([]);
  const [relatorioTotal, setRelatorioTotal] = useState(null);
  const [relatorioInativos, setRelatorioInativos] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tipoRelatorio, setTipoRelatorio] = useState("turma");

  useEffect(() => {
    fetchTurmas();
  }, []);

  const fetchTurmas = async () => {
    try {
      const response = await api.get('/turmas');
      // Ordenar turmas alfabeticamente
      const turmasOrdenadas = response.data.sort((a, b) => 
        a.nome.localeCompare(b.nome, 'pt-BR')
      );
      setTurmas(turmasOrdenadas);
    } catch (error) {
      toast.error("Erro ao carregar turmas");
    }
  };

  const gerarRelatorioTurma = async () => {
    if (!selectedTurma) {
      toast.error("Selecione uma turma");
      return;
    }

    setLoading(true);
    try {
      
      const response = await api.get(`/relatorios/turma/${selectedTurma}`);
      setRelatorio(response.data);
      setAlunosFaltosos([]);
    } catch (error) {
      toast.error("Erro ao gerar relatório");
    } finally {
      setLoading(false);
    }
  };

  const gerarRelatorioFaltosos = async () => {
    setLoading(true);
    try {
      
      const response = await api.get('/relatorios/alunos-faltosos');
      setAlunosFaltosos(response.data);
      setRelatorio(null);
      setRelatorioTotal(null);
    } catch (error) {
      toast.error("Erro ao gerar relatório");
    } finally {
      setLoading(false);
    }
  };

  const gerarRelatorioTotal = async () => {
    setLoading(true);
    try {
      
      const response = await api.get('/relatorios/total-alunos');
      setRelatorioTotal(response.data);
      setRelatorio(null);
      setAlunosFaltosos([]);
      setRelatorioInativos(null);
    } catch (error) {
      toast.error("Erro ao gerar relatório");
    } finally {
      setLoading(false);
    }
  };

  const gerarRelatorioInativos = async () => {
    setLoading(true);
    try {
      
      const response = await api.get('/relatorios/alunos-inativos');
      setRelatorioInativos(response.data);
      setRelatorio(null);
      setAlunosFaltosos([]);
      setRelatorioTotal(null);
    } catch (error) {
      toast.error("Erro ao gerar relatório");
    } finally {
      setLoading(false);
    }
  };

  const exportarPDF = async (turmaId) => {
    try {
      const response = await api.get(`/export/relatorio-turma/${turmaId}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `relatorio_turma.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("PDF exportado com sucesso!");
    } catch (error) {
      toast.error("Erro ao exportar PDF");
    }
  };

  const exportarExcel = async (turmaId) => {
    try {
      const response = await api.get(`/export/relatorio-turma/${turmaId}/excel`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `relatorio_turma.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Excel exportado com sucesso!");
    } catch (error) {
      toast.error("Erro ao exportar Excel");
    }
  };

  return (
    <PasswordProtection page="relatorios" onLogout={onLogout}>
      <Layout onLogout={onLogout}>
        <div className="space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk' }}>Relatórios</h1>
          <p className="text-gray-600">Visualize relatórios e estatísticas</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Gerar Relatório</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Tipo de Relatório</Label>
              <Select value={tipoRelatorio} onValueChange={(value) => {
                setTipoRelatorio(value);
                setRelatorio(null);
                setAlunosFaltosos([]);
                setRelatorioTotal(null);
              }}>
                <SelectTrigger data-testid="tipo-relatorio-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="turma">Relatório por Turma</SelectItem>
                  <SelectItem value="faltosos">Alunos Faltosos (Geral)</SelectItem>
                  <SelectItem value="total">Total de Alunos</SelectItem>
                  <SelectItem value="inativos">Alunos Inativos</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {tipoRelatorio === "turma" && (
              <div className="space-y-2">
                <Label>Turma</Label>
                <Select value={selectedTurma} onValueChange={setSelectedTurma}>
                  <SelectTrigger data-testid="turma-select">
                    <SelectValue placeholder="Selecione uma turma" />
                  </SelectTrigger>
                  <SelectContent>
                    {turmas.map((turma) => (
                      <SelectItem key={turma.id} value={turma.id}>
                        {turma.nome} - {turma.modalidade}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <Button
              data-testid="gerar-relatorio-button"
              onClick={() => {
                if (tipoRelatorio === "turma") gerarRelatorioTurma();
                else if (tipoRelatorio === "faltosos") gerarRelatorioFaltosos();
                else if (tipoRelatorio === "total") gerarRelatorioTotal();
                else if (tipoRelatorio === "inativos") gerarRelatorioInativos();
              }}
              disabled={loading}
              className="w-full bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600"
            >
              <FileText className="w-4 h-4 mr-2" />
              {loading ? "Gerando..." : "Gerar Relatório"}
            </Button>
          </CardContent>
        </Card>

        {relatorio && (
          <Card data-testid="relatorio-turma">
            <CardHeader>
              <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
                <CardTitle className="text-base sm:text-lg break-words">Relatório da Turma: {relatorio.turma.nome}</CardTitle>
                <div className="flex gap-2 shrink-0">
                  <Button
                    data-testid="exportar-pdf-button"
                    onClick={() => exportarPDF(relatorio.turma.id)}
                    variant="outline"
                    size="sm"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    PDF
                  </Button>
                  <Button
                    data-testid="exportar-excel-button"
                    onClick={() => exportarExcel(relatorio.turma.id)}
                    variant="outline"
                    size="sm"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Excel
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-4">
                    <div className="text-sm text-blue-600 mb-1">Modalidade</div>
                    <div className="text-2xl font-bold text-blue-800">{relatorio.turma.modalidade}</div>
                  </CardContent>
                </Card>
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="p-4">
                    <div className="text-sm text-green-600 mb-1">Faixa Etária</div>
                    <div className="text-2xl font-bold text-green-800">{relatorio.turma.faixa_etaria}</div>
                  </CardContent>
                </Card>
                <Card className="bg-orange-50 border-orange-200">
                  <CardContent className="p-4">
                    <div className="text-sm text-orange-600 mb-1">Total de Alunos</div>
                    <div className="text-2xl font-bold text-orange-800" data-testid="relatorio-total-alunos">{relatorio.total_alunos}</div>
                  </CardContent>
                </Card>
              </div>

              <div>
                <h3 className="font-semibold text-lg mb-3">Alunos da Turma</h3>
                <div className="space-y-2">
                  {relatorio.alunos.map((aluno) => (
                    <div key={aluno.id} className="p-3 border rounded-lg" data-testid={`relatorio-aluno-${aluno.id}`}>
                      <div className="font-medium">{aluno.nome}</div>
                      <div className="text-sm text-gray-600">CPF: {aluno.cpf} | Telefone: {aluno.telefone}</div>
                    </div>
                  ))}
                </div>
              </div>

              {relatorio.alunos_faltosos.length > 0 && (
                <div>
                  <h3 className="font-semibold text-lg mb-3 flex items-center gap-2 text-red-600">
                    <AlertTriangle className="w-5 h-5" />
                    Alunos com Faltas (últimos 30 dias)
                  </h3>
                  <div className="space-y-2">
                    {relatorio.alunos_faltosos.map((aluno) => (
                      <div key={aluno.id} className="p-3 border border-red-200 rounded-lg bg-red-50" data-testid={`faltoso-relatorio-${aluno.id}`}>
                        <div className="flex justify-between items-center">
                          <div>
                            <div className="font-medium">{aluno.nome}</div>
                            <div className="text-sm text-gray-600">CPF: {aluno.cpf}</div>
                          </div>
                          <Badge variant="destructive" data-testid={`faltas-count-${aluno.id}`}>{aluno.num_faltas} faltas</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {alunosFaltosos.length > 0 && (
          <Card data-testid="relatorio-faltosos">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                Relatório de Alunos Faltosos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {alunosFaltosos.map((aluno) => (
                  <Card key={aluno.id} className="border-red-200 bg-red-50" data-testid={`faltoso-geral-${aluno.id}`}>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h4 className="font-semibold text-lg mb-2">{aluno.nome}</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
                            <div><strong>CPF:</strong> {aluno.cpf}</div>
                            <div><strong>Telefone:</strong> {aluno.telefone}</div>
                            <div><strong>Responsável:</strong> {aluno.responsavel.nome}</div>
                            <div><strong>Contato:</strong> {aluno.responsavel.telefone}</div>
                          </div>
                        </div>
                        <Badge variant="destructive" className="text-lg px-4 py-2">
                          {aluno.num_faltas} faltas
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {relatorioTotal && (
          <Card data-testid="relatorio-total">
            <CardHeader>
              <CardTitle>Relatório Total de Alunos</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-4">
                    <div className="text-sm text-blue-600 mb-1">Total de Alunos</div>
                    <div className="text-3xl font-bold text-blue-800" data-testid="total-geral-alunos">{relatorioTotal.total_geral}</div>
                  </CardContent>
                </Card>
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="p-4">
                    <div className="text-sm text-green-600 mb-1">Total de Turmas</div>
                    <div className="text-3xl font-bold text-green-800" data-testid="total-turmas-relatorio">{relatorioTotal.total_turmas}</div>
                  </CardContent>
                </Card>
                <Card className="bg-orange-50 border-orange-200">
                  <CardContent className="p-4">
                    <div className="text-sm text-orange-600 mb-1">Sem Turma</div>
                    <div className="text-3xl font-bold text-orange-800" data-testid="total-sem-turma">{relatorioTotal.total_sem_turma}</div>
                  </CardContent>
                </Card>
              </div>
              
              <div>
                <h3 className="font-semibold text-lg mb-3">Distribuição por Turma</h3>
                <div className="space-y-3">
                  {relatorioTotal.alunos_por_turma.map((item) => (
                    <Card key={item.turma.id} className="border" data-testid={`turma-total-${item.turma.id}`}>
                      <CardContent className="p-4">
                        <div className="flex justify-between items-center mb-2">
                          <div>
                            <div className="font-semibold text-lg">{item.turma.nome}</div>
                            <div className="text-sm text-gray-600">{item.turma.modalidade} - {item.turma.faixa_etaria}</div>
                          </div>
                          <Badge variant="outline" className="text-base" data-testid={`turma-count-${item.turma.id}`}>{item.alunos.length} alunos</Badge>
                        </div>
                        <div className="mt-3 space-y-1">
                          {item.alunos.map((aluno) => (
                            <div key={aluno.id} className="text-sm text-gray-600 pl-3" data-testid={`aluno-item-${aluno.id}`}>
                              • {aluno.nome}
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              {relatorioTotal.alunos_sem_turma.length > 0 && (
                <div>
                  <h3 className="font-semibold text-lg mb-3 text-orange-600">Alunos sem Turma</h3>
                  <Card className="border-orange-200 bg-orange-50">
                    <CardContent className="p-4">
                      <div className="space-y-2">
                        {relatorioTotal.alunos_sem_turma.map((aluno) => (
                          <div key={aluno.id} className="text-sm" data-testid={`aluno-sem-turma-${aluno.id}`}>
                            • {aluno.nome} - {aluno.cpf}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {relatorioInativos && (
          <Card data-testid="relatorio-inativos">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                Relatório de Alunos Inativos
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <Card className="bg-red-50 border-red-200">
                  <CardContent className="p-4">
                    <div className="text-sm text-red-600 mb-1">Total de Alunos Inativos</div>
                    <div className="text-3xl font-bold text-red-800">{relatorioInativos.total}</div>
                  </CardContent>
                </Card>
              </div>

              {relatorioInativos.alunos.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  Nenhum aluno inativo encontrado
                </div>
              ) : (
                <div className="space-y-3">
                  {relatorioInativos.alunos.map((aluno) => (
                    <Card key={aluno.id} className="border-red-200 bg-red-50">
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h4 className="font-semibold text-lg">{aluno.nome}</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600 mt-2">
                              <div><strong>CPF:</strong> {aluno.cpf}</div>
                              <div><strong>Telefone:</strong> {aluno.telefone}</div>
                              <div><strong>Data Nascimento:</strong> {new Date(aluno.data_nascimento).toLocaleDateString('pt-BR')}</div>
                              <div><strong>Responsável:</strong> {aluno.responsavel.nome}</div>
                              <div><strong>Contato:</strong> {aluno.responsavel.telefone}</div>
                              {aluno.turmas_nomes && aluno.turmas_nomes.length > 0 && (
                                <div className="md:col-span-2">
                                  <strong>Turmas:</strong> {aluno.turmas_nomes.join(', ')}
                                </div>
                              )}
                            </div>
                          </div>
                          <Badge variant="destructive">Inativo</Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
    </PasswordProtection>
  );
}
